import { useCallback, useEffect, useRef, useState } from "react";
import {
  consumeOdooLiveChatAutoOpen,
  fetchOdooLiveChatIdentityToken,
  OdooLiveChatStatus,
  openOdooLiveChat,
  requestOdooLiveChatLoad,
  setOdooLiveChatAutoOpen,
  whenOdooLiveChatReady
} from "services/odoo-live-chat/odoo-live-chat";

import {
  ClLivechatApi,
  IUseOdooLiveChatConnect,
  IUseOdooLiveChatConnectParams,
  PanelState
} from "./use-odoo-live-chat-connect.types";

export const LOAD_TIMEOUT_MS = 10000;
export const RETRY_FEEDBACK_MS = 600;

// Closed by the working hours gets its own panel; anything else is a generic failure.
const toOfflinePanel = (status: OdooLiveChatStatus | null): PanelState =>
  status?.reason === "outside_hours" ? "closed" : "offline";

const openChat = async (api: ClLivechatApi) => {
  // Identity first, so the welcome message can use the student's name.
  const token = await fetchOdooLiveChatIdentityToken();
  if (token) await api.setIdentity(token);
  await openOdooLiveChat();
};

// Opens the chat on click. The Odoo widget is loaded on the first click; while
// it loads, or when nobody is available, `panel` says which panel to show.
// "Coba lagi" re-checks availability without the CDN cache and, when the
// widget was loaded while offline, reloads the page and reopens the chat.
export const useOdooLiveChatConnect = ({
  isShown,
  serverStatus,
  serverAvailable,
  refreshFresh,
  markOpened
}: IUseOdooLiveChatConnectParams): IUseOdooLiveChatConnect => {
  const [panel, setPanel] = useState<PanelState | null>(null);
  const [shouldAutoConnect, setShouldAutoConnect] = useState(false);
  const cancelPendingOpen = useRef<() => void>(() => undefined);

  // Coming back from the reload done by "Coba lagi": open the chat right away.
  useEffect(() => {
    if (consumeOdooLiveChatAutoOpen()) setShouldAutoConnect(true);
  }, []);

  useEffect(() => () => cancelPendingOpen.current(), []);

  const connect = useCallback(() => {
    cancelPendingOpen.current();
    setPanel("connecting");
    const timeout = setTimeout(
      () => setPanel(toOfflinePanel(serverStatus)),
      LOAD_TIMEOUT_MS
    );
    const removeReadyListener = whenOdooLiveChatReady(async (api) => {
      clearTimeout(timeout);
      if (!api.isAvailable()) {
        setPanel(toOfflinePanel(serverStatus));
        return;
      }
      await openChat(api);
      setPanel(null);
    });
    cancelPendingOpen.current = () => {
      clearTimeout(timeout);
      removeReadyListener();
    };
    requestOdooLiveChatLoad();
  }, [serverStatus]);

  useEffect(() => {
    if (shouldAutoConnect && isShown) {
      setShouldAutoConnect(false);
      connect();
    }
  }, [shouldAutoConnect, isShown, connect]);

  const closePanel = useCallback(() => {
    cancelPendingOpen.current();
    setPanel(null);
  }, []);

  const handleClick = useCallback(() => {
    markOpened();

    const api = window.clLivechat;
    if (api?.isReady()) {
      if (api.isAvailable()) openChat(api);
      else setPanel(toOfflinePanel(serverStatus));
      return;
    }
    // Known offline: no need to download the widget just to say so.
    if (serverAvailable === false) {
      setPanel(toOfflinePanel(serverStatus));
      return;
    }
    connect();
  }, [markOpened, serverAvailable, serverStatus, connect]);

  const handleRetry = useCallback(async () => {
    cancelPendingOpen.current();
    setPanel("connecting");
    const [status] = await Promise.all([
      refreshFresh(),
      new Promise((resolve) => setTimeout(resolve, RETRY_FEEDBACK_MS))
    ]);
    if (status?.available === false) {
      setPanel(toOfflinePanel(status));
    } else if (
      window.clLivechat?.isReady() &&
      !window.clLivechat.isAvailable()
    ) {
      // Odoo only initializes the widget at load time; pick up the new state.
      setOdooLiveChatAutoOpen();
      window.location.reload();
    } else {
      connect();
    }
  }, [refreshFresh, connect]);

  return { panel, handleClick, handleRetry, closePanel };
};
